import { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isNil, has, noop, isFunction, first, isArray, isEqual, isEmpty } from 'lodash';
// Import Types
import type { FilterTagRef, HolderProps } from 'types';
import type { BaseRequestBody } from 'hooks/useListFilters';
// Import Hooks
import useQueryParams from 'hooks/UseQueryParams';
import useFilterFieldsState from 'hooks/UseFilterFieldsState';
import useApplyOnEnter from 'hooks/UseApplyOnEnter';
// Import UI Components
import { Button } from '@geneui/components';
// Import Components
import HolderWrapper from 'components/common/HolderWrapper';
import FilterFields from 'components/common/FilterFields';
// Import Services
import { FilterHolderComponentsTypes, FiltersFieldsMap } from 'components/common/FilterFields/service';
// Import Constants
import { l } from 'constants/common';

type GenUIHolderProps = Omit<
    HolderProps,
    'title' | 'position' | 'expandText' | 'collapseText' | 'disableOnHover' | 'footer' | 'disabled' | 'children'
>;

export interface FilterHolderDefaultInputData {
    type: string;
    valueFieldKey: string;
    displayName: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
}

export interface FilterHolderProps extends GenUIHolderProps {
    defaultInputsData: FilterHolderDefaultInputData[];
    filterHolderKey: string;
    isDisabled?: boolean;
    isApplyOnEnterEnabled?: boolean;
    filterTagRef?: React.RefObject<FilterTagRef>;
    withQueryParams?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onApply?: (filters: any) => void;
    onReset?: () => void;
    opened: GenUIHolderProps['opened'];
    onChange: GenUIHolderProps['onChange'];
    queryParams?: Record<string, string | string[]>;
    storedFilters?: BaseRequestBody['Filters'];
}

export interface FilterHolderRef {
    filtersState: AnyObject[];
    changeAndApplyFilters: (filters) => void;
}

/**
 * @component
 *
 * @namespace
 * @property {array}  defaultInputsData      - The default values for parties.
 * @property {object}  defaultInputsData.item  - Example of the defaultInputsData item.
 */
const FilterHolder = forwardRef<FilterHolderRef, FilterHolderProps>(
    (
        {
            defaultInputsData,
            onApply,
            onReset = noop,
            isDisabled,
            isApplyOnEnterEnabled = true,
            filterHolderKey,
            filterTagRef,
            withQueryParams = true,
            queryParams = {},
            storedFilters,
            ...restProps
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const { decode, historyReplace } = useQueryParams(filterHolderKey);
        const {
            filters,
            appliedFilters,
            changeFilters,
            applyFilters,
            resetFilters,
            hasUnappliedChanges,
            isResetDisabled,
            isApplyDisabled,
        } = useFilterFieldsState(defaultInputsData);

        const changeFiltersHandler = (
            filters,
            isProcessSupportedValues = false,
            changeApplyBtnDisabledState = true,
        ) => {
            let tmpFilters = filters;
            if (isProcessSupportedValues) {
                tmpFilters = filters.map((filterField) => {
                    if (filterField.type === FilterHolderComponentsTypes.multiSelect.keyName) {
                        if (!isNil(filterField?.data) && isArray(filterField.data)) {
                            const supportedValues = filterField.data.reduce((acc, item) => [...acc, item.value], []);
                            filterField.value = filterField?.value.filter((item) => {
                                return supportedValues.includes(item);
                            });
                        } else {
                            filterField.value = [];
                        }
                    }

                    return filterField;
                });
            }
            changeFilters(tmpFilters, changeApplyBtnDisabledState);
            return tmpFilters;
        };

        const init = () => {
            const params = withQueryParams ? decode() : {};
            const hasSearchParamsValue = params ? filters.find((data) => has(params, data.valueFieldKey)) : -1;

            let resultFilterInputsData = filters;

            if (hasSearchParamsValue !== -1) {
                resultFilterInputsData = filters.map((filterInputData, index) => {
                    const { valueFieldKey, type } = filterInputData;
                    if (!isNil(params[valueFieldKey]) && params[valueFieldKey] !== '') {
                        const isMultiSelect = type === FilterHolderComponentsTypes.multiSelect.keyName;
                        if (type === FilterHolderComponentsTypes.rangeDate.keyName || isMultiSelect) {
                            filterInputData.value = params[valueFieldKey];

                            if (isMultiSelect) {
                                filterInputData.value = params[valueFieldKey].map((value) => {
                                    return +value;
                                });
                            }
                        } else if (type === FilterHolderComponentsTypes.checkbox.keyName) {
                            filterInputData.checked = params[valueFieldKey] === 'true';
                        } else {
                            filterInputData.value = params[valueFieldKey];
                        }
                    } else {
                        filterInputData.value = defaultInputsData[index].value;
                    }

                    return filterInputData;
                });
                changeFiltersHandler(resultFilterInputsData);
                applyFilters();
            }
            setParamsToUrl(false, resultFilterInputsData);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(init, [storedFilters]);

        /**
         * The useEffect below updates internal filters when external query params change.
         * Keeps the FilterHolder UI in sync with the URL without remounting.
         * This ensures that the FilterHolder UI always reflects the current
         * URL state, without requiring the component to unmount/remount.
         */
        useEffect(() => {
            if (isEmpty(queryParams)) return;

            const isDifferent = !isEqual(
                queryParams,
                FiltersFieldsMap(filters), // current internal state mapped to same structure
            );

            if (!isDifferent) return;

            const updatedFilters = filters.map((filterInputData) => {
                const { valueFieldKey, type } = filterInputData;
                const newValue = queryParams[valueFieldKey];

                if (newValue === undefined) return filterInputData;

                if (type === FilterHolderComponentsTypes.multiSelect.keyName) {
                    filterInputData.value = isArray(newValue) ? newValue.map(Number) : [];
                } else if (type === FilterHolderComponentsTypes.checkbox.keyName) {
                    filterInputData.checked = newValue === 'true';
                } else {
                    filterInputData.value = newValue;
                }

                return filterInputData;
            });

            changeFiltersHandler(updatedFilters);
            applyFilters();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [queryParams]);

        const setParamsToUrl = (withApply, data = filters) => {
            const resultData = FiltersFieldsMap(data);

            if (withQueryParams) {
                historyReplace(resultData);
            }

            if (withApply) {
                applyFilters();
                isFunction(onApply) && onApply(resultData);
            }
        };

        const handleReset = () => {
            const resetList = cloneDeep(filters);

            resetList.forEach((filterField) => {
                if (has(filterField, 'value')) {
                    filterField.value = '';
                }
            });

            resetFilters();
            setParamsToUrl(false, resetList);
            isFunction(onReset) && onReset();
        };

        const handleApply = () => {
            setParamsToUrl(true);
        };

        // `hasUnappliedChanges` is used instead of `isApplyDisabled`, which is updated with a debounce
        const { containerRef, handleInteraction } = useApplyOnEnter<HTMLDivElement>({
            onApply: handleApply,
            canApply: () => !isDisabled && hasUnappliedChanges(),
            isEnabled: isApplyOnEnterEnabled,
        });

        const handleChange = (e, changeInputValue, customChange, index, filterInputData) => {
            const resultData = changeInputValue(e, customChange, cloneDeep(filters), index, filterInputData);
            changeFiltersHandler(resultData);
        };

        useEffect(() => {
            if (!isDisabled) {
                const mergedData = filters.map((filterField) => {
                    filterField.data = first(
                        defaultInputsData.filter(({ valueFieldKey }) => {
                            return valueFieldKey === filterField.valueFieldKey;
                        }),
                    )?.data;

                    return filterField;
                });

                changeFiltersHandler(mergedData, true);
                applyFilters();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isDisabled]);

        useImperativeHandle(ref, () => ({
            changeAndApplyFilters: (filters) => {
                const tmpFilters = changeFiltersHandler(filters, true, false);
                setParamsToUrl(true, tmpFilters);
            },
            filtersState: appliedFilters,
        }));

        useEffect(() => {
            if (!isDisabled) {
                filterTagRef?.current?.changeFiltersList?.(appliedFilters);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [appliedFilters]);

        return (
            <HolderWrapper
                ref={containerRef}
                isDisabled={isDisabled}
                title={t(l.Filters)}
                position="left"
                expandText={t(l.Expand)}
                collapseText={t(l.Collapse)}
                disableOnHover={true}
                footer={
                    <>
                        <Button
                            appearance="minimal"
                            color="default"
                            flexibility="full-width"
                            onClick={handleReset}
                            disabled={isResetDisabled}
                        >
                            {t(l.Reset)}
                        </Button>

                        <Button
                            color="confirm"
                            flexibility="full-width"
                            onClick={handleApply}
                            disabled={isApplyDisabled}
                        >
                            {t(l.Apply)}
                        </Button>
                    </>
                }
                onFocus={handleInteraction}
                onMouseDown={handleInteraction}
                {...restProps}
            >
                {!isDisabled && <FilterFields filterInputsData={filters} handleChange={handleChange} />}
            </HolderWrapper>
        );
    },
);

FilterHolder.displayName = 'FilterHolder';

export default FilterHolder;
